import { useEffect, useState } from "react";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { BiBlock, BiCheck, BiError } from "react-icons/bi";
import logo from "shared/assets/images/logo.svg";
import { NAME_SPACES } from "shared/locales/constants";
import { API_ORIGIN } from "utils/helpers/apiOrigin";
import { formatLocalDateTime } from "utils/helpers/date";
import { fill } from "utils/helpers/template";
import {
  Created,
  saveKeyFile,
  useKeyPair,
} from "../../Private/Admin/Organizations/Detail/ApiAccess";
import {
  ImportFlow,
  Step,
} from "../../Private/Admin/Organizations/Detail/EncryptionKey";

const KINDS = {
  ENCRYPTION_KEY: "ENCRYPTION_KEY",
  API_KEY: "API_KEY",
  API_SECRET: "API_SECRET",
};

const UNAVAILABLE = "HANDOVER_UNAVAILABLE";

const TOKEN_PARAM = "token";

const request = async (path, { token, method = "GET", body }) => {
  const response = await fetch(`${API_ORIGIN}/api/handovers${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(isNil(body) ? {} : { "Content-Type": "application/json" }),
    },
    body: isNil(body) ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;
  throw new Error(get(data, "error.code", "HANDOVER_FAILED"));
};

const ErrorNotice = ({ text }) => (
  <p className="onboarding__notice onboarding__notice--danger onboarding__notice--compact">
    <BiError size={18} aria-hidden="true" />
    <span>{text}</span>
  </p>
);

ErrorNotice.propTypes = {
  text: PropTypes.string.isRequired,
};

const SecretHandover = ({ token, labels, onFailure }) => {
  const PAGE = labels.HANDOVER.PAGE;
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null);

  const reveal = () => {
    setBusy(true);
    return request("/secrets", { token, method: "POST" })
      .then(setRevealed)
      .catch(onFailure)
      .finally(() => setBusy(false));
  };

  if (revealed)
    return (
      <Created
        created={{ ...revealed, privateKey: null }}
        access={revealed.connection}
        labels={labels.API}
      />
    );

  return (
    <div className="onboarding__stack">
      <p className="onboarding__notice onboarding__notice--warning onboarding__notice--compact">
        <BiError size={18} aria-hidden="true" />
        <span>{PAGE.REVEAL_NOTE}</span>
      </p>
      <div className="onboarding__footer onboarding__footer--flat">
        <button
          type="button"
          className="onboarding__button onboarding__button--primary onboarding__push"
          disabled={busy}
          onClick={reveal}
        >
          {busy ? PAGE.REVEALING : PAGE.REVEAL}
        </button>
      </div>
    </div>
  );
};

SecretHandover.propTypes = {
  token: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  onFailure: PropTypes.func.isRequired,
};

const KeyHandover = ({ token, labels, onFailure }) => {
  const PAGE = labels.HANDOVER.PAGE;
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const keyPair = useKeyPair({ labels: labels.API, busy });

  const submit = () => {
    setBusy(true);
    return request("/keys", {
      token,
      method: "POST",
      body: { publicKey: keyPair.publicKey },
    })
      .then(({ credential, connection }) => {
        if (keyPair.privateKey)
          saveKeyFile({ credential, privateKey: keyPair.privateKey });
        setCreated({
          credential,
          connection,
          privateKey: keyPair.privateKey,
        });
      })
      .catch(onFailure)
      .finally(() => setBusy(false));
  };

  if (created)
    return (
      <Created
        created={created}
        access={created.connection}
        labels={labels.API}
      />
    );

  return (
    <div className="onboarding__stack">
      <ol className="onboarding__step-list">
        {keyPair.steps.map((step, index) => (
          <Step key={step.key} number={index + 1} title={step.title}>
            {step.body}
          </Step>
        ))}
      </ol>
      <div className="onboarding__footer onboarding__footer--flat">
        <button
          type="button"
          className="onboarding__button onboarding__button--primary onboarding__push"
          disabled={isNil(keyPair.publicKey) || busy}
          onClick={submit}
        >
          {busy ? PAGE.SUBMITTING : PAGE.SUBMIT_KEY}
        </button>
      </div>
    </div>
  );
};

KeyHandover.propTypes = {
  token: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  onFailure: PropTypes.func.isRequired,
};

const EncryptionHandover = ({ token, details, labels, onFailure }) => {
  const PAGE = labels.HANDOVER.PAGE;
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const call = (path, body) => {
    setBusy(true);
    return request(path, { token, method: "POST", body })
      .catch((error) => {
        onFailure(error);
        return null;
      })
      .finally(() => setBusy(false));
  };

  if (done)
    return (
      <div className="onboarding__banner onboarding__banner--success">
        <span className="onboarding__banner-icon" aria-hidden="true">
          <BiCheck size={20} />
        </span>
        <span className="onboarding__banner-text">
          <span className="onboarding__banner-title">{PAGE.DONE_TITLE}</span>
          <span>{PAGE.DONE_TEXT}</span>
        </span>
      </div>
    );

  return (
    <ImportFlow
      encryption={{ importValidTo: get(details, "encryption.importValidTo") }}
      labels={labels.ENCRYPTION}
      reimport={Boolean(get(details, "encryption.reimport"))}
      busy={busy}
      onPrepare={() => call("/import-parameters")}
      onImport={(material) =>
        call("/imports", material).then((result) => {
          if (!isNil(result)) setDone(true);
        })
      }
    />
  );
};

EncryptionHandover.propTypes = {
  token: PropTypes.string.isRequired,
  details: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  onFailure: PropTypes.func.isRequired,
};

const CONTENTS = {
  [KINDS.API_SECRET]: SecretHandover,
  [KINDS.API_KEY]: KeyHandover,
  [KINDS.ENCRYPTION_KEY]: EncryptionHandover,
};

const ERROR_LABELS = {
  [KINDS.API_SECRET]: "API",
  [KINDS.API_KEY]: "API",
  [KINDS.ENCRYPTION_KEY]: "ENCRYPTION",
};

const Handover = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const DETAIL = t("DETAIL", { returnObjects: true });
  const PAGE = DETAIL.HANDOVER.PAGE;
  const [searchParams, setSearchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get(TOKEN_PARAM) || "");
  const [details, setDetails] = useState(null);
  const [unavailable, setUnavailable] = useState(!token);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (searchParams.has(TOKEN_PARAM)) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    request("", { token })
      .then((loaded) => active && setDetails(loaded))
      .catch(() => active && setUnavailable(true));
    return () => {
      active = false;
    };
  }, [token]);

  const kind = get(details, "kind");
  const labels = {
    API: {
      ...DETAIL.API,
      HANDOVER: DETAIL.HANDOVER,
      CREATED: { ...DETAIL.API.CREATED, ...PAGE.CREATED },
    },
    ENCRYPTION: {
      ...DETAIL.ENCRYPTION,
      HANDOVER: DETAIL.HANDOVER,
      IMPORT: { ...DETAIL.ENCRYPTION.IMPORT, HANDOVER: PAGE.SEED_KEEP },
    },
    HANDOVER: DETAIL.HANDOVER,
  };

  const onFailure = (failure) => {
    const code = get(failure, "message");
    if (code === UNAVAILABLE) return setUnavailable(true);
    const errors = {
      ...get(labels, [ERROR_LABELS[kind], "ERRORS"], {}),
      ...PAGE.ERRORS,
    };
    return setError(get(errors, code, PAGE.ERROR));
  };

  const Content = get(CONTENTS, kind);
  const values = {
    label: get(details, "credential.label", ""),
    organization: get(details, "organization.name", ""),
  };

  return (
    <div className="onboarding">
      <header className="onboarding__header">
        <div className="onboarding__header-inner onboarding__header-inner--plain">
          <div className="onboarding__heading">
            <span className="onboarding__eyebrow">{PAGE.EYEBROW}</span>
            <h1 className="onboarding__title">
              {values.organization || PAGE.TITLE}
            </h1>
          </div>
          <img src={logo} alt={PAGE.LOGO_ALT} className="onboarding__brand" />
        </div>
      </header>
      <div className="onboarding__body onboarding__body--single">
        <section aria-labelledby="handover-title" className="onboarding__main">
          <div className="onboarding__main-head">
            <h2 id="handover-title" className="onboarding__main-title">
              {unavailable
                ? PAGE.UNAVAILABLE_TITLE
                : fill(get(PAGE.TITLES, kind, PAGE.TITLE), values)}
            </h2>
            {!unavailable && details ? (
              <>
                <p className="onboarding__lead">
                  {fill(get(PAGE.LEADS, kind, ""), values)}
                </p>
                <p className="onboarding__hint">
                  {fill(PAGE.EXPIRES, {
                    date: formatLocalDateTime(details.expiresAt),
                  })}
                </p>
              </>
            ) : null}
          </div>
          <div className="onboarding__content">
            {unavailable ? (
              <p className="onboarding__notice onboarding__notice--danger">
                <BiBlock size={18} aria-hidden="true" />
                <span>{PAGE.UNAVAILABLE_TEXT}</span>
              </p>
            ) : null}
            {!unavailable && isNil(details) ? (
              <p className="onboarding__paragraph">{PAGE.LOADING}</p>
            ) : null}
            {!unavailable && error ? <ErrorNotice text={error} /> : null}
            {!unavailable && Content ? (
              <Content
                token={token}
                details={details}
                labels={labels}
                onFailure={onFailure}
              />
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Handover;
